import { z } from 'zod';
import { getResourceTypeService, ResourceTypeEnum, ServiceEnum } from './resources.js';

/**
 * Canonical list of actions a policy statement can allow.
 *
 * Naming: `<service>:<resource-type>:<verb>`, lowercase, hyphen-separated.
 * Every action is registered in {@link ACTION_CATALOG} with the resource type it applies to,
 * its label, whether it's sensitive, and what it requires.
 */
export enum PolicyActionEnum {
  ConsoleAccess = 'mgmt:console:access',

  AccountView = 'mgmt:account:view',
  AccountManage = 'mgmt:account:manage',

  AgentView = 'mgmt:agent:view',
  AgentManage = 'mgmt:agent:manage',

  ApplicationView = 'mgmt:application:view',
  ApplicationManage = 'mgmt:application:manage',

  AuditLogView = 'mgmt:audit-log:view',

  AuthAppView = 'mgmt:auth-app:view',
  AuthAppManage = 'mgmt:auth-app:manage',

  BrandView = 'mgmt:brand:view',
  BrandManage = 'mgmt:brand:manage',

  ChatContextView = 'mgmt:chat-context:view',
  ChatContextManage = 'mgmt:chat-context:manage',

  ConnectedAppView = 'mgmt:connected-app:view',
  ConnectedAppManage = 'mgmt:connected-app:manage',

  CrmConnectionView = 'mgmt:crm-connection:view',
  CrmConnectionManage = 'mgmt:crm-connection:manage',

  DashboardView = 'mgmt:dashboard:view',
  DashboardManage = 'mgmt:dashboard:manage',

  DataConnectionView = 'mgmt:data-connection:view',
  DataConnectionManage = 'mgmt:data-connection:manage',

  DataSourceView = 'mgmt:data-source:view',
  DataSourceManage = 'mgmt:data-source:manage',

  FilterView = 'mgmt:filter:view',
  FilterManage = 'mgmt:filter:manage',

  FormView = 'mgmt:form:view',
  FormManage = 'mgmt:form:manage',

  PolicyView = 'mgmt:policy:view',
  PolicyManage = 'mgmt:policy:manage',
  PolicyGrantAny = 'mgmt:policy:grant-any',

  PromptView = 'mgmt:prompt:view',
  PromptManage = 'mgmt:prompt:manage',

  PromptQueryLogView = 'mgmt:prompt-query-log:view',

  RoleView = 'mgmt:role:view',
  RoleManage = 'mgmt:role:manage',

  SecretView = 'mgmt:secret:view',
  SecretManage = 'mgmt:secret:manage',

  ToolView = 'mgmt:tool:view',
  ToolManage = 'mgmt:tool:manage',

  UnmetNeedView = 'mgmt:unmet-need:view',
  UnmetNeedManage = 'mgmt:unmet-need:manage',

  UserView = 'mgmt:user:view',
  UserManage = 'mgmt:user:manage',
  UserAssign = 'mgmt:user:assign',

  ChatTraceView = 'chat:trace:view',
  ChatConversationExport = 'chat:conversation:export',
  ChatConversationShare = 'chat:conversation:share',
  ChatConversationCopy = 'chat:conversation:copy',

  HcpProfileViewAll = 'hcp:profile:view-all',

  IeProjectCreate = 'ie:project:create',
  IeProjectManageDetails = 'ie:project:manage-details',
  IeProjectManageGroupTags = 'ie:project:manage-group-tags',
  IeProjectViewEntities = 'ie:project:view-entities',
  IeProjectManageEntities = 'ie:project:manage-entities',
  IeProjectViewHighlights = 'ie:project:view-highlights',
  IeProjectViewTags = 'ie:project:view-tags',
  IeProjectCreateTags = 'ie:project:create-tags',
  IeProjectEditTags = 'ie:project:edit-tags',
  IeProjectDeleteTags = 'ie:project:delete-tags',
  IeProjectViewFilesTab = 'ie:project:view-files-tab',
  IeProjectViewSummary = 'ie:project:view-summary',
  IeProjectRefreshSummary = 'ie:project:refresh-summary',
  IeProjectViewWidgets = 'ie:project:view-widgets',
  IeProjectAddWidgets = 'ie:project:add-widgets',
  IeProjectUpdateWidgets = 'ie:project:update-widgets',
  IeProjectDeleteWidgets = 'ie:project:delete-widgets',

  IeFileManageGroupTags = 'ie:file:manage-group-tags',
  IeFileUpload = 'ie:file:upload',
  IeFilePublish = 'ie:file:publish',
  IeFileViewUnpublished = 'ie:file:view-unpublished',
  IeFileViewSummary = 'ie:file:view-summary',
  IeFileRefreshSummary = 'ie:file:refresh-summary',
  IeFileDelete = 'ie:file:delete',

  IeBrandCreate = 'ie:brand:create',
  IeBrandDelete = 'ie:brand:delete',
  IeBrandViewFilesTab = 'ie:brand:view-files-tab',
  IeBrandViewProjectsTab = 'ie:brand:view-projects-tab',
  IeBrandViewProjectTrackerTab = 'ie:brand:view-project-tracker-tab',

  IeFolderManageGroupTags = 'ie:folder:manage-group-tags',
  IeFolderDelete = 'ie:folder:delete',

  IeQuantAnalysisView = 'ie:quant-analysis:view',
  IeQuantAnalysisEdit = 'ie:quant-analysis:edit',
  IeQuantAnalysisDelete = 'ie:quant-analysis:delete',
  IeQuantAnalysisPublish = 'ie:quant-analysis:publish',
  IeQuantAnalysisViewUnpublished = 'ie:quant-analysis:view-unpublished',

  IeSegmentView = 'ie:segment:view',
  IeSegmentCreate = 'ie:segment:create',
  IeSegmentEdit = 'ie:segment:edit',
  IeSegmentDelete = 'ie:segment:delete',
  IeSegmentPublish = 'ie:segment:publish',
  IeSegmentViewUnpublished = 'ie:segment:view-unpublished',

  IePatientJourneyView = 'ie:patient-journey:view',
  IePatientJourneyManage = 'ie:patient-journey:manage',
  IePatientJourneyCreate = 'ie:patient-journey:create',
  IePatientJourneyDelete = 'ie:patient-journey:delete',
  IePatientJourneyPublish = 'ie:patient-journey:publish',
  IePatientJourneyViewUnpublished = 'ie:patient-journey:view-unpublished',

  IeDashboardView = 'ie:dashboard:view',
  IeDashboardCreate = 'ie:dashboard:create',
  IeDashboardEdit = 'ie:dashboard:edit',
  IeDashboardDelete = 'ie:dashboard:delete',
  IeDashboardPublish = 'ie:dashboard:publish',
  IeDashboardViewUnpublished = 'ie:dashboard:view-unpublished',

  IeTranscriptManageGroupTags = 'ie:transcript:manage-group-tags',
  IeTranscriptPublish = 'ie:transcript:publish',
  IeTranscriptViewUnpublished = 'ie:transcript:view-unpublished',
  IeTranscriptMarkGoodQuotes = 'ie:transcript:mark-good-quotes',
  IeTranscriptApplyModifications = 'ie:transcript:apply-modifications',
  IeTranscriptViewSummary = 'ie:transcript:view-summary',
  IeTranscriptRefreshSummary = 'ie:transcript:refresh-summary',
  IeTranscriptViewEntities = 'ie:transcript:view-entities',
  IeTranscriptManageEntities = 'ie:transcript:manage-entities',
  IeTranscriptViewHighlights = 'ie:transcript:view-highlights',
  IeTranscriptCreateHighlights = 'ie:transcript:create-highlights',
  IeTranscriptEditHighlights = 'ie:transcript:edit-highlights',
  IeTranscriptDeleteHighlights = 'ie:transcript:delete-highlights',
  IeTranscriptViewTaggedMoments = 'ie:transcript:view-tagged-moments',
  IeTranscriptCreateTaggedMoments = 'ie:transcript:create-tagged-moments',
  IeTranscriptEditTaggedMoments = 'ie:transcript:edit-tagged-moments',
  IeTranscriptDeleteTaggedMoments = 'ie:transcript:delete-tagged-moments',
  IeTranscriptViewComments = 'ie:transcript:view-comments',
  IeTranscriptComment = 'ie:transcript:comment',
  IeTranscriptEditComments = 'ie:transcript:edit-comments',
  IeTranscriptDeleteComments = 'ie:transcript:delete-comments',

  IeGlobalSearchView = 'ie:global-search:view',

  QueueDashboardView = 'ops:queue-dashboard:view',
}
export const PolicyAction = z.enum(PolicyActionEnum);
export type PolicyAction = z.infer<typeof PolicyAction>;

/** An action's place in its resource's access level, for UIs that offer None / View / Manage. */
export enum ActionTierEnum {
  View = 'view',
  Manage = 'manage',
}

export type ActionDefinition = {
  resourceType: ResourceTypeEnum;
  /**
   * A cluster of related actions on the resource, such as "Tags" or "Summary", that editors
   * show together on their own row. Absent for actions on the resource itself. Grouped actions
   * are individual checkboxes (`tier: null`); `requires` still ties a dependent one to its View.
   */
  group?: string;
  /**
   * Short name for UIs, unique within the resource type's group (or among its ungrouped
   * actions), e.g. "View" or "Assign brands".
   */
  label: string;
  description: string;
  /**
   * Lets the holder change who can do what, or reach credentials. UIs flag it when it's
   * granted. Required, so every new action gets a deliberate answer.
   */
  sensitive: boolean;
  /** The access level this action stands for on its resource; null for any other action, such as every InsightEdge one. */
  tier: ActionTierEnum | null;
  /**
   * Actions this one is of little use without, on top of its service's entry action (see
   * {@link SERVICE_ENTRY_ACTIONS}). Editors grant or warn about them; the evaluator doesn't
   * expand them, so a policy document always lists exactly what it grants.
   */
  requires?: PolicyActionEnum[];
};

export const ACTION_CATALOG: Record<PolicyActionEnum, ActionDefinition> = {
  [PolicyActionEnum.ConsoleAccess]: {
    resourceType: ResourceTypeEnum.Console,
    label: 'Sign in',
    description: 'Sign in to the management app',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.AccountView]: {
    resourceType: ResourceTypeEnum.Account,
    label: 'View',
    description: 'View accounts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AccountManage]: {
    resourceType: ResourceTypeEnum.Account,
    label: 'Manage',
    description: 'Create, update and delete accounts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AccountView],
  },

  [PolicyActionEnum.AgentView]: {
    resourceType: ResourceTypeEnum.Agent,
    label: 'View',
    description: 'View agents and skills',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AgentManage]: {
    resourceType: ResourceTypeEnum.Agent,
    label: 'Manage',
    description: 'Create, update and delete agents and skills',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AgentView],
  },

  [PolicyActionEnum.ApplicationView]: {
    resourceType: ResourceTypeEnum.Application,
    label: 'View',
    description: 'View applications',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ApplicationManage]: {
    resourceType: ResourceTypeEnum.Application,
    label: 'Manage',
    description: 'Create, update and delete applications',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ApplicationView],
  },

  [PolicyActionEnum.AuditLogView]: {
    resourceType: ResourceTypeEnum.AuditLog,
    label: 'View',
    description: 'View audit logs',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.AuthAppView]: {
    resourceType: ResourceTypeEnum.AuthApp,
    label: 'View',
    description: 'View authentication apps',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.AuthAppManage]: {
    resourceType: ResourceTypeEnum.AuthApp,
    label: 'Manage',
    description: 'Create, update and delete authentication apps',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.AuthAppView],
  },

  [PolicyActionEnum.BrandView]: {
    resourceType: ResourceTypeEnum.Brand,
    label: 'View',
    description: 'View brands',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.BrandManage]: {
    resourceType: ResourceTypeEnum.Brand,
    label: 'Manage',
    description: 'Create, update and delete brands',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.BrandView],
  },

  [PolicyActionEnum.ChatContextView]: {
    resourceType: ResourceTypeEnum.ChatContext,
    label: 'View',
    description: 'View chat contexts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ChatContextManage]: {
    resourceType: ResourceTypeEnum.ChatContext,
    label: 'Manage',
    description: 'Create, update and delete chat contexts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ChatContextView],
  },

  [PolicyActionEnum.ConnectedAppView]: {
    resourceType: ResourceTypeEnum.ConnectedApp,
    label: 'View',
    description: 'View connected apps',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ConnectedAppManage]: {
    resourceType: ResourceTypeEnum.ConnectedApp,
    label: 'Manage',
    description: 'Create, update and delete connected apps',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ConnectedAppView],
  },

  [PolicyActionEnum.CrmConnectionView]: {
    resourceType: ResourceTypeEnum.CrmConnection,
    label: 'View',
    description: 'View CRM connections',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.CrmConnectionManage]: {
    resourceType: ResourceTypeEnum.CrmConnection,
    label: 'Manage',
    description: 'Create, update and delete CRM connections',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.CrmConnectionView],
  },

  [PolicyActionEnum.DashboardView]: {
    resourceType: ResourceTypeEnum.Dashboard,
    label: 'View',
    description: 'View dashboards and pages',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DashboardManage]: {
    resourceType: ResourceTypeEnum.Dashboard,
    label: 'Manage',
    description: 'Create, update and delete dashboards and pages',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DashboardView],
  },

  [PolicyActionEnum.DataConnectionView]: {
    resourceType: ResourceTypeEnum.DataConnection,
    label: 'View',
    description: 'View data connections',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DataConnectionManage]: {
    resourceType: ResourceTypeEnum.DataConnection,
    label: 'Manage',
    description: 'Create, update and delete data connections',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DataConnectionView],
  },

  [PolicyActionEnum.DataSourceView]: {
    resourceType: ResourceTypeEnum.DataSource,
    label: 'View',
    description: 'View data sources',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.DataSourceManage]: {
    resourceType: ResourceTypeEnum.DataSource,
    label: 'Manage',
    description: 'Create, update and delete data sources',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.DataSourceView],
  },

  [PolicyActionEnum.FilterView]: {
    resourceType: ResourceTypeEnum.Filter,
    label: 'View',
    description: 'View filters',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.FilterManage]: {
    resourceType: ResourceTypeEnum.Filter,
    label: 'Manage',
    description: 'Create, update and delete filters',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.FilterView],
  },

  [PolicyActionEnum.FormView]: {
    resourceType: ResourceTypeEnum.Form,
    label: 'View',
    description: 'View form schemas and workflows',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.FormManage]: {
    resourceType: ResourceTypeEnum.Form,
    label: 'Manage',
    description: 'Create, update and delete form schemas and workflows',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.FormView],
  },

  [PolicyActionEnum.PolicyView]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'View',
    description: 'View access policies',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.PolicyManage]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'Manage',
    description: 'Create, update and delete customer-managed access policies',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.PolicyView],
  },
  [PolicyActionEnum.PolicyGrantAny]: {
    resourceType: ResourceTypeEnum.Policy,
    label: 'Grant any action',
    description:
      'Grant any action through policies, roles and imports, including actions the granter does not hold themselves',
    sensitive: true,
    tier: null,
  },

  [PolicyActionEnum.PromptView]: {
    resourceType: ResourceTypeEnum.Prompt,
    label: 'View',
    description: 'View prompts',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.PromptManage]: {
    resourceType: ResourceTypeEnum.Prompt,
    label: 'Manage',
    description: 'Create, update and delete prompts',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.PromptView],
  },

  [PolicyActionEnum.PromptQueryLogView]: {
    resourceType: ResourceTypeEnum.PromptQueryLog,
    label: 'View',
    description: 'View the prompt query log',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.RoleView]: {
    resourceType: ResourceTypeEnum.Role,
    label: 'View',
    description: 'View roles and the policies attached to them',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.RoleManage]: {
    resourceType: ResourceTypeEnum.Role,
    label: 'Manage',
    description: 'Create, update and delete customer-managed roles',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.RoleView],
  },

  [PolicyActionEnum.SecretView]: {
    resourceType: ResourceTypeEnum.Secret,
    label: 'View',
    description: 'View managed secrets',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.SecretManage]: {
    resourceType: ResourceTypeEnum.Secret,
    label: 'Manage',
    description: 'Create, update and delete managed secrets',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.SecretView],
  },

  [PolicyActionEnum.ToolView]: {
    resourceType: ResourceTypeEnum.Tool,
    label: 'View',
    description: 'View tools',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.ToolManage]: {
    resourceType: ResourceTypeEnum.Tool,
    label: 'Manage',
    description: 'Create and update tools',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.ToolView],
  },

  [PolicyActionEnum.UnmetNeedView]: {
    resourceType: ResourceTypeEnum.UnmetNeed,
    label: 'View',
    description: 'View unmet needs',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.UnmetNeedManage]: {
    resourceType: ResourceTypeEnum.UnmetNeed,
    label: 'Manage',
    description: 'Create, update and delete unmet needs',
    sensitive: false,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.UnmetNeedView],
  },

  [PolicyActionEnum.UserView]: {
    resourceType: ResourceTypeEnum.User,
    label: 'View',
    description: 'View users',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
  [PolicyActionEnum.UserManage]: {
    resourceType: ResourceTypeEnum.User,
    label: 'Manage',
    description: 'Create and update users and their policies',
    sensitive: true,
    tier: ActionTierEnum.Manage,
    requires: [PolicyActionEnum.UserView],
  },
  [PolicyActionEnum.UserAssign]: {
    resourceType: ResourceTypeEnum.User,
    label: 'Assign brands',
    description: 'Add and remove brands on users',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.UserView],
  },

  [PolicyActionEnum.ChatTraceView]: {
    resourceType: ResourceTypeEnum.ChatTrace,
    label: 'View',
    description: 'View LLM trace links in chat',
    sensitive: false,
    tier: ActionTierEnum.View,
  },

  [PolicyActionEnum.HcpProfileViewAll]: {
    resourceType: ResourceTypeEnum.HcpProfile,
    label: 'View all HCPs',
    description: 'View all HCPs, not only those assigned to the user',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.ChatConversationExport]: {
    resourceType: ResourceTypeEnum.ChatConversation,
    label: 'Export',
    description: 'Download chats and responses, and export charts as CSV, image or PowerPoint',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.ChatConversationShare]: {
    resourceType: ResourceTypeEnum.ChatConversation,
    label: 'Share',
    description: 'Share chats with other users',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.ChatConversationCopy]: {
    resourceType: ResourceTypeEnum.ChatConversation,
    label: 'Copy',
    description: 'Copy chat messages (hides the copy buttons; cannot be enforced on the server)',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.IeProjectCreate]: {
    resourceType: ResourceTypeEnum.IeProject,
    label: 'Create',
    description: 'Create projects',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectManageDetails]: {
    resourceType: ResourceTypeEnum.IeProject,
    label: 'Manage details',
    description: "Update a project's details",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectManageGroupTags]: {
    resourceType: ResourceTypeEnum.IeProject,
    label: 'Manage group tags',
    description: 'Manage group tags on projects',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectViewEntities]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Entities',
    label: 'View',
    description: "View a project's entities",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectManageEntities]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Entities',
    label: 'Manage',
    description: "Add, update and remove a project's entities",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewEntities],
  },
  [PolicyActionEnum.IeProjectViewHighlights]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Highlights',
    label: 'View',
    description: "View a project's highlights",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectViewTags]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Tags',
    label: 'View',
    description: "View a project's tags",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectCreateTags]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Tags',
    label: 'Create',
    description: 'Create tags on projects',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewTags],
  },
  [PolicyActionEnum.IeProjectEditTags]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Tags',
    label: 'Edit',
    description: 'Edit tags on projects',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewTags],
  },
  [PolicyActionEnum.IeProjectDeleteTags]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Tags',
    label: 'Delete',
    description: 'Delete tags from projects',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewTags],
  },
  [PolicyActionEnum.IeProjectViewFilesTab]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Files',
    label: 'View',
    description: "Open a project's Files tab",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectViewSummary]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Summary',
    label: 'View',
    description: "View a project's summary",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectRefreshSummary]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Summary',
    label: 'Refresh',
    description: "Refresh (regenerate) a project's summary",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewSummary],
  },
  [PolicyActionEnum.IeProjectViewWidgets]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Widgets',
    label: 'View',
    description: 'View the widgets on a project',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeProjectAddWidgets]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Widgets',
    label: 'Add',
    description: 'Add widgets to a project',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewWidgets],
  },
  [PolicyActionEnum.IeProjectUpdateWidgets]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Widgets',
    label: 'Update',
    description: "Change a project's saved widgets",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewWidgets],
  },
  [PolicyActionEnum.IeProjectDeleteWidgets]: {
    resourceType: ResourceTypeEnum.IeProject,
    group: 'Widgets',
    label: 'Delete',
    description: "Delete widgets from a project's library",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeProjectViewWidgets],
  },

  [PolicyActionEnum.IeFileManageGroupTags]: {
    resourceType: ResourceTypeEnum.IeFile,
    label: 'Manage group tags',
    description: 'Manage group tags on files',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeFilePublish]: {
    resourceType: ResourceTypeEnum.IeFile,
    label: 'Publish',
    description: 'Publish files',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeFileViewUnpublished],
  },
  [PolicyActionEnum.IeFileViewUnpublished]: {
    resourceType: ResourceTypeEnum.IeFile,
    label: 'View unpublished',
    description: 'View unpublished (draft) files',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeFileViewSummary]: {
    resourceType: ResourceTypeEnum.IeFile,
    group: 'Summary',
    label: 'View',
    description: "View a file's summary",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeFileRefreshSummary]: {
    resourceType: ResourceTypeEnum.IeFile,
    group: 'Summary',
    label: 'Refresh',
    description: "Refresh (regenerate) a file's summary",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeFileViewSummary],
  },
  [PolicyActionEnum.IeFileUpload]: {
    resourceType: ResourceTypeEnum.IeFile,
    label: 'Upload',
    description: 'Upload files to brands, projects and folders',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeFileDelete]: {
    resourceType: ResourceTypeEnum.IeFile,
    label: 'Delete',
    description: 'Delete files',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.IeBrandCreate]: {
    resourceType: ResourceTypeEnum.IeBrand,
    label: 'Create',
    description: 'Create brands in InsightEdge',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeBrandDelete]: {
    resourceType: ResourceTypeEnum.IeBrand,
    label: 'Delete',
    description: 'Delete brands in InsightEdge',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeBrandViewFilesTab]: {
    resourceType: ResourceTypeEnum.IeBrand,
    group: 'Files',
    label: 'View',
    description: "Open a brand's Files tab",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeBrandViewProjectsTab]: {
    resourceType: ResourceTypeEnum.IeBrand,
    group: 'Projects',
    label: 'View',
    description: "Open a brand's Projects tab",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeBrandViewProjectTrackerTab]: {
    resourceType: ResourceTypeEnum.IeBrand,
    group: 'Project tracker',
    label: 'View',
    description: "Open a brand's Project Tracker tab",
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.IeFolderManageGroupTags]: {
    resourceType: ResourceTypeEnum.IeFolder,
    label: 'Manage group tags',
    description: 'Manage group tags on folders',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeFolderDelete]: {
    resourceType: ResourceTypeEnum.IeFolder,
    label: 'Delete',
    description: 'Delete folders',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.IeQuantAnalysisView]: {
    resourceType: ResourceTypeEnum.IeQuantAnalysis,
    label: 'View',
    description: 'View quant analyses',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeQuantAnalysisEdit]: {
    resourceType: ResourceTypeEnum.IeQuantAnalysis,
    label: 'Edit',
    description: 'Edit quant analyses',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeQuantAnalysisView],
  },
  [PolicyActionEnum.IeQuantAnalysisDelete]: {
    resourceType: ResourceTypeEnum.IeQuantAnalysis,
    label: 'Delete',
    description: 'Delete quant analyses, and their items and folders',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeQuantAnalysisView],
  },
  [PolicyActionEnum.IeQuantAnalysisPublish]: {
    resourceType: ResourceTypeEnum.IeQuantAnalysis,
    label: 'Publish',
    description: 'Publish quant analyses',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeQuantAnalysisView, PolicyActionEnum.IeQuantAnalysisViewUnpublished],
  },
  [PolicyActionEnum.IeQuantAnalysisViewUnpublished]: {
    resourceType: ResourceTypeEnum.IeQuantAnalysis,
    label: 'View unpublished',
    description: 'View unpublished (draft) quant analyses',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeQuantAnalysisView],
  },

  [PolicyActionEnum.IeSegmentView]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'View',
    description: 'View segments',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeSegmentCreate]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'Create',
    description: 'Create segments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeSegmentView],
  },
  [PolicyActionEnum.IeSegmentEdit]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'Edit',
    description: 'Edit segments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeSegmentView],
  },
  [PolicyActionEnum.IeSegmentDelete]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'Delete',
    description: 'Delete segments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeSegmentView],
  },
  [PolicyActionEnum.IeSegmentPublish]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'Publish',
    description: 'Publish segments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeSegmentView, PolicyActionEnum.IeSegmentViewUnpublished],
  },
  [PolicyActionEnum.IeSegmentViewUnpublished]: {
    resourceType: ResourceTypeEnum.IeSegment,
    label: 'View unpublished',
    description: 'View unpublished (draft) segments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeSegmentView],
  },

  [PolicyActionEnum.IePatientJourneyView]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'View',
    description: 'View patient journeys',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IePatientJourneyManage]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'Manage',
    description: 'Update patient journeys',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IePatientJourneyView],
  },
  [PolicyActionEnum.IePatientJourneyCreate]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'Create',
    description: 'Create patient journeys',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IePatientJourneyView],
  },
  [PolicyActionEnum.IePatientJourneyDelete]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'Delete',
    description: 'Delete patient journeys',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IePatientJourneyView],
  },
  [PolicyActionEnum.IePatientJourneyPublish]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'Publish',
    description: 'Publish patient journeys',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IePatientJourneyView, PolicyActionEnum.IePatientJourneyViewUnpublished],
  },
  [PolicyActionEnum.IePatientJourneyViewUnpublished]: {
    resourceType: ResourceTypeEnum.IePatientJourney,
    label: 'View unpublished',
    description: 'View unpublished (draft) patient journeys',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IePatientJourneyView],
  },

  [PolicyActionEnum.IeDashboardView]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'View',
    description: 'View dashboard tabs on projects and brands',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeDashboardCreate]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'Create',
    description: 'Add dashboard tabs to projects and brands',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeDashboardView],
  },
  [PolicyActionEnum.IeDashboardEdit]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'Edit',
    description: 'Edit dashboard tabs on projects and brands',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeDashboardView],
  },
  [PolicyActionEnum.IeDashboardDelete]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'Delete',
    description: 'Delete dashboard tabs from projects and brands',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeDashboardView],
  },
  [PolicyActionEnum.IeDashboardPublish]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'Publish',
    description: 'Publish and unpublish dashboard tabs on projects and brands',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeDashboardView, PolicyActionEnum.IeDashboardViewUnpublished],
  },
  [PolicyActionEnum.IeDashboardViewUnpublished]: {
    resourceType: ResourceTypeEnum.IeDashboard,
    label: 'View unpublished',
    description: 'View unpublished (draft) dashboard tabs on projects and brands',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeDashboardView],
  },

  [PolicyActionEnum.IeTranscriptManageGroupTags]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    label: 'Manage group tags',
    description: 'Manage group tags on transcripts',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptPublish]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    label: 'Publish',
    description: 'Publish transcripts',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewUnpublished],
  },
  [PolicyActionEnum.IeTranscriptViewUnpublished]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    label: 'View unpublished',
    description: 'View unpublished (draft) transcripts',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptMarkGoodQuotes]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    label: 'Mark good quotes',
    description: 'Mark quotes in a transcript as good quotes',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptApplyModifications]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    label: 'Apply modifications',
    description:
      'Change a transcript itself: edit its text (including find and replace, and entity edits that rewrite it) and change its speakers',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptViewSummary]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Summary',
    label: 'View',
    description: "View a transcript's summary",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptRefreshSummary]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Summary',
    label: 'Refresh',
    description: "Refresh (regenerate) a transcript's summary",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewSummary],
  },
  [PolicyActionEnum.IeTranscriptViewEntities]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Entities',
    label: 'View',
    description: "View a transcript's entities",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptManageEntities]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Entities',
    label: 'Manage',
    description: "Add, update and remove a transcript's entities",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewEntities],
  },
  [PolicyActionEnum.IeTranscriptViewHighlights]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Highlights',
    label: 'View',
    description: 'View the highlights applied to a transcript (not tagged moments)',
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptCreateHighlights]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Highlights',
    label: 'Create',
    description: 'Highlight text in a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewHighlights],
  },
  [PolicyActionEnum.IeTranscriptEditHighlights]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Highlights',
    label: 'Edit',
    description: 'Change a transcript highlight, such as its color',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewHighlights],
  },
  [PolicyActionEnum.IeTranscriptDeleteHighlights]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Highlights',
    label: 'Delete',
    description: 'Remove a highlight from a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewHighlights],
  },
  [PolicyActionEnum.IeTranscriptViewTaggedMoments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Tagged moments',
    label: 'View',
    description:
      "View a transcript's tagged moments: the Highlights tab, and the tagged moments highlighted in the transcript",
    sensitive: false,
    tier: null,
  },
  [PolicyActionEnum.IeTranscriptCreateTaggedMoments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Tagged moments',
    label: 'Create',
    description: 'Tag a moment in a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewTaggedMoments],
  },
  [PolicyActionEnum.IeTranscriptEditTaggedMoments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Tagged moments',
    label: 'Edit',
    description: "Change a transcript's tagged moment, such as its tag or range",
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewTaggedMoments],
  },
  [PolicyActionEnum.IeTranscriptDeleteTaggedMoments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Tagged moments',
    label: 'Delete',
    description: 'Remove a tagged moment from a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewTaggedMoments],
  },
  [PolicyActionEnum.IeTranscriptViewComments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Comments',
    label: 'View',
    description: 'View comments on a transcript',
    sensitive: false,
    tier: null,
  },
  // The create action. Its id predates the split into view / create / edit / delete and is kept, since
  // policies store action ids as plain strings.
  [PolicyActionEnum.IeTranscriptComment]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Comments',
    label: 'Create',
    description: 'Comment on a transcript, and reply to comments',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewComments],
  },
  [PolicyActionEnum.IeTranscriptEditComments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Comments',
    label: 'Edit',
    description: 'Edit your own comments on a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewComments],
  },
  [PolicyActionEnum.IeTranscriptDeleteComments]: {
    resourceType: ResourceTypeEnum.IeTranscript,
    group: 'Comments',
    label: 'Delete',
    description: 'Delete your own comments on a transcript',
    sensitive: false,
    tier: null,
    requires: [PolicyActionEnum.IeTranscriptViewComments],
  },

  [PolicyActionEnum.IeGlobalSearchView]: {
    resourceType: ResourceTypeEnum.IeGlobalSearch,
    label: 'View',
    description: 'Use global search: the search bar in the navigation and the search page',
    sensitive: false,
    tier: null,
  },

  [PolicyActionEnum.QueueDashboardView]: {
    resourceType: ResourceTypeEnum.QueueDashboard,
    label: 'View',
    description: 'Open the background job queue dashboard',
    sensitive: false,
    tier: ActionTierEnum.View,
  },
};

/**
 * The action a service's other actions are unusable without, such as signing in to the
 * management app. Null when the service has no such gate.
 */
export const SERVICE_ENTRY_ACTIONS: Record<ServiceEnum, PolicyActionEnum | null> = {
  [ServiceEnum.Management]: PolicyActionEnum.ConsoleAccess,
  [ServiceEnum.Chat]: null,
  [ServiceEnum.Hcp]: null,
  [ServiceEnum.InsightEdge]: null,
  [ServiceEnum.Operations]: null,
};

/** Every action `action` needs to be useful: its own requirements and its service's entry action. */
export function getActionRequirements(action: PolicyActionEnum): PolicyActionEnum[] {
  const entry = SERVICE_ENTRY_ACTIONS[getResourceTypeService(ACTION_CATALOG[action].resourceType)];
  const requires = ACTION_CATALOG[action].requires ?? [];
  return entry !== null && entry !== action && !requires.includes(entry) ? [entry, ...requires] : requires;
}

export const ALL_ACTIONS: readonly PolicyActionEnum[] = Object.values(PolicyActionEnum);
